import React from "react";
import styled, { useTheme } from "styled-components";
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  oneOfType,
  string,
} from "prop-types";

import Typography from "@components/shared/Typography";
import RequestButton from "@components/shared/RequestButton";
import TestDrivePointLogo from "./TestDrivePointLogo";

import { useTranslation } from "@lib/i18n";
import { testDriveType } from "@types";

import {
  color,
  fontSize,
  fontWeight,
  hyphenate,
  prop,
} from "@components/shared/utils";

const WrapperContainer = styled.div`
  display: flex;
  padding: 1rem 0;
  border-bottom: 1px solid ${color("grey")};
  color: ${color("black")};
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-left: 1rem;
`;

const Distance = styled(Typography)`
  font-size: ${fontSize("md")};
  margin-left: auto;
  font-weight: ${fontWeight("bold")};
  text-transform: uppercase;
`;

const StyledRequestButton = styled(RequestButton)`
  padding: 0.75rem 0.5rem;
  margin: 0;

  &:enabled:hover {
    background: ${color("white")};
    color: ${color("amiOrange")};
  }
`;

const Name = styled(Typography)`
  color: ${prop("color")};
  font-size: ${fontSize("md")};
  font-weight: ${fontWeight("bold")};
  text-transform: uppercase;
  padding-left: 1rem;
`;

const FilterResultItemWrapper = ({
  name,
  testDriveType,
  children,
  distanceInMeters,
  isActive,
  accentColor,
  onPick,
  onPickSuccess,
  storeType,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const convertToKilometers = (distanceInMeters, precision = 2) =>
    (distanceInMeters / 1000).toFixed(precision);

  return (
    <WrapperContainer>
      <LogoContainer>
        <TestDrivePointLogo
          testDriveType={testDriveType}
          storeType={storeType}
        />
      </LogoContainer>
      <ContentContainer>
        <TitleContainer>
          <Name
            data-id={`point-name-${hyphenate(name, /\.|\s/g)}`}
            color={
              testDriveType?.colorHexa
                ? `#${testDriveType.colorHexa}`
                : theme.colors.grey
            }
          >
            {name}
          </Name>
          {!!distanceInMeters && (
            <Distance>{`${convertToKilometers(distanceInMeters)} ${t(
              "general.kilometerSymbol"
            )}`}</Distance>
          )}
        </TitleContainer>
        <DetailContainer>
          {children}
          <StyledRequestButton
            withBorder
            inverted={!isActive}
            data-id="filtered-result-item-select-button"
            customColor={accentColor}
            triggerRequestCallback={onPick}
            onRequestSuccess={onPickSuccess}
          >
            {isActive ? t("testDrive.selected") : t("testDrive.select")}
          </StyledRequestButton>
        </DetailContainer>
      </ContentContainer>
    </WrapperContainer>
  );
};

FilterResultItemWrapper.propTypes = {
  testDriveType: testDriveType,
  name: string.isRequired,
  children: oneOfType([node, arrayOf(node)]),
  distanceInMeters: number,
  isActive: bool,
  accentColor: string.isRequired,
  onPick: func.isRequired,
  onPickSuccess: func,
  storeType: string,
};

export default FilterResultItemWrapper;
